import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import type {
  ChecklistItem,
  EvidenceDocument,
  Finding,
  ProjectTask,
  ProjectMember,
  ProjectMemberListResponse,
  TaskLinkedObjectType,
  TaskListResponse,
  TaskPriority,
  TaskResponse,
  TaskStatus,
  ProjectRole,
} from '../types';

interface ProjectTasksProps {
  projectId: string;
  currentUserId?: string;
  checklistItems: ChecklistItem[];
  findings: Finding[];
  evidenceDocuments: EvidenceDocument[];
  projectRole: ProjectRole | null;
  onTasksChanged?: () => void;
}

interface LinkOption {
  value: string;
  label: string;
}

const PRIORITIES: TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

export default function ProjectTasks({
  projectId,
  currentUserId,
  checklistItems,
  findings,
  evidenceDocuments,
  projectRole,
  onTasksChanged,
}: ProjectTasksProps) {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [dueDate, setDueDate] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState(currentUserId ?? '');
  const [linkedObject, setLinkedObject] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completionNotes, setCompletionNotes] = useState<
    Record<string, string>
  >({});
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (currentUserId) {
      setAssigneeUserId(existing => existing || currentUserId);
    }
  }, [currentUserId]);

  useEffect(() => {
    let canceled = false;
    setIsLoading(true);
    setError(null);
    Promise.all([
      api.get<TaskListResponse>(`/api/projects/${projectId}/tasks`),
      api.get<ProjectMemberListResponse>(`/api/projects/${projectId}/members`),
    ])
      .then(([taskResponse, memberResponse]) => {
        if (!canceled) {
          setTasks(taskResponse.data.tasks);
          setMembers(memberResponse.data.members);
        }
      })
      .catch(() => {
        if (!canceled) setError(t('results.tasks.loadError'));
      })
      .finally(() => {
        if (!canceled) setIsLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [projectId, reloadToken, t]);

  const linkOptions = useMemo<LinkOption[]>(() => {
    const options: LinkOption[] = [];
    checklistItems.forEach(item => {
      options.push({
        value: `checklist_item:${item.id}`,
        label: `${t('results.tasks.linkTypes.checklist_item')}: ${item.title}`,
      });
    });
    findings
      .filter(finding => finding.id)
      .forEach(finding => {
        options.push({
          value: `finding:${finding.id}`,
          label: `${t('results.tasks.linkTypes.finding')}: ${finding.title}`,
        });
      });
    evidenceDocuments.forEach(document => {
      options.push({
        value: `evidence_document:${document.id}`,
        label: `${t('results.tasks.linkTypes.evidence_document')}: ${document.filename}`,
      });
    });
    return options;
  }, [checklistItems, evidenceDocuments, findings, t]);

  const openTaskCount = tasks.filter(
    task => task.status !== 'completed'
  ).length;
  const canManageTasks =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    projectRole === 'reviewer';

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim()) return;

    let linkedObjectType: TaskLinkedObjectType | undefined;
    let linkedObjectId: string | undefined;
    if (linkedObject) {
      const separator = linkedObject.indexOf(':');
      linkedObjectType = linkedObject.slice(
        0,
        separator
      ) as TaskLinkedObjectType;
      linkedObjectId = linkedObject.slice(separator + 1);
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await api.post<TaskResponse>(
        `/api/projects/${projectId}/tasks`,
        {
          title: title.trim(),
          description: description.trim(),
          priority,
          dueDate: dueDate || undefined,
          assigneeUserId: assigneeUserId.trim() || undefined,
          linkedObjectType,
          linkedObjectId,
        }
      );
      setTasks(existing => [response.data.task, ...existing]);
      setTitle('');
      setDescription('');
      setPriority('medium');
      setDueDate('');
      setLinkedObject('');
      onTasksChanged?.();
    } catch {
      setError(t('results.tasks.createError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateStatus = async (task: ProjectTask, status: TaskStatus) => {
    setUpdatingTaskId(task.id);
    setError(null);
    try {
      const response = await api.patch<TaskResponse>(
        `/api/projects/${projectId}/tasks/${task.id}`,
        {
          status,
          completionNote:
            status === 'completed' ? (completionNotes[task.id] ?? '') : '',
        }
      );
      setTasks(existing =>
        existing.map(item =>
          item.id === response.data.task.id ? response.data.task : item
        )
      );
      onTasksChanged?.();
    } catch {
      setError(t('results.tasks.updateError'));
    } finally {
      setUpdatingTaskId(null);
    }
  };

  return (
    <section
      id="project-tasks"
      className="results-panel"
      style={{ marginBottom: 24, padding: '24px 28px' }}
      aria-labelledby="project-tasks-heading"
    >
      <div className="results-toolbar">
        <div>
          <h2
            id="project-tasks-heading"
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 16,
              color: 'var(--text-primary)',
            }}
          >
            {t('results.tasks.title')}
          </h2>
          <p
            style={{
              margin: '6px 0 0',
              color: 'var(--text-muted)',
              fontSize: 13,
            }}
          >
            {t('results.tasks.subtitle', { count: openTaskCount })}
          </p>
        </div>
        {error && (
          <button
            type="button"
            className="button button-secondary"
            onClick={() => setReloadToken(token => token + 1)}
          >
            {t('results.tasks.retry')}
          </button>
        )}
      </div>

      {error && (
        <p role="alert" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      {canManageTasks && (
        <form
          onSubmit={handleCreate}
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(min(100%, 210px), 1fr))',
            gap: 12,
            margin: '20px 0',
            padding: 16,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-secondary)',
          }}
        >
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              {t('results.tasks.fields.title')}
            </span>
            <input
              value={title}
              maxLength={200}
              required
              onChange={event => setTitle(event.target.value)}
            />
          </label>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              {t('results.tasks.fields.priority')}
            </span>
            <select
              value={priority}
              onChange={event =>
                setPriority(event.target.value as TaskPriority)
              }
            >
              {PRIORITIES.map(value => (
                <option key={value} value={value}>
                  {t(`results.tasks.priority.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              {t('results.tasks.fields.dueDate')}
            </span>
            <input
              type="date"
              value={dueDate}
              onChange={event => setDueDate(event.target.value)}
            />
          </label>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              {t('results.tasks.fields.assignee')}
            </span>
            <select
              value={assigneeUserId}
              onChange={event => setAssigneeUserId(event.target.value)}
            >
              <option value="">{t('results.tasks.unassigned')}</option>
              {members.map(member => (
                <option key={member.userId} value={member.userId}>
                  {member.name || member.email}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              {t('results.tasks.fields.linkedObject')}
            </span>
            <select
              value={linkedObject}
              onChange={event => setLinkedObject(event.target.value)}
            >
              <option value="">{t('results.tasks.fields.noLink')}</option>
              {linkOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              {t('results.tasks.fields.description')}
            </span>
            <textarea
              value={description}
              maxLength={2000}
              rows={2}
              onChange={event => setDescription(event.target.value)}
            />
          </label>
          <button
            className="button button-primary"
            type="submit"
            disabled={isSubmitting || !title.trim()}
            style={{ justifySelf: 'start' }}
          >
            {isSubmitting
              ? t('results.tasks.creating')
              : t('results.tasks.create')}
          </button>
        </form>
      )}

      {isLoading ? (
        <p style={{ color: 'var(--text-muted)' }}>
          {t('results.tasks.loading')}
        </p>
      ) : tasks.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('results.tasks.empty')}</p>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {tasks.map(task => (
            <article
              key={task.id}
              style={{
                padding: 16,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-card)',
                opacity: task.status === 'completed' ? 0.68 : 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>
                    {task.title}
                  </strong>
                  <div
                    style={{
                      marginTop: 5,
                      color: 'var(--text-muted)',
                      fontSize: 12,
                    }}
                  >
                    {t(`results.tasks.priority.${task.priority}`)} ·{' '}
                    {t(`results.tasks.status.${task.status}`)}
                    {task.dueDate &&
                      ` · ${t('results.tasks.due', {
                        date: new Date(
                          `${task.dueDate.slice(0, 10)}T00:00:00`
                        ).toLocaleDateString(),
                      })}`}
                  </div>
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                  {task.assigneeUserId === currentUserId
                    ? t('results.tasks.assignedToYou')
                    : task.assigneeName || t('results.tasks.unassigned')}
                </span>
              </div>
              {task.description && (
                <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  {task.description}
                </p>
              )}
              {task.completionNote && (
                <p style={{ color: 'var(--success)', fontSize: 13 }}>
                  {task.completionNote}
                </p>
              )}
              {task.status !== 'completed' && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 12,
                  }}
                >
                  {task.status === 'open' && (
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={
                        updatingTaskId === task.id ||
                        (!canManageTasks &&
                          task.assigneeUserId !== currentUserId)
                      }
                      onClick={() => void updateStatus(task, 'in_progress')}
                    >
                      {t('results.tasks.start')}
                    </button>
                  )}
                  <input
                    value={completionNotes[task.id] ?? ''}
                    maxLength={2000}
                    placeholder={t('results.tasks.completionNote')}
                    onChange={event =>
                      setCompletionNotes(existing => ({
                        ...existing,
                        [task.id]: event.target.value,
                      }))
                    }
                    style={{ flex: '1 1 240px' }}
                  />
                  <button
                    type="button"
                    className="button button-primary"
                    disabled={
                      updatingTaskId === task.id ||
                      (!canManageTasks && task.assigneeUserId !== currentUserId)
                    }
                    onClick={() => void updateStatus(task, 'completed')}
                  >
                    {t('results.tasks.complete')}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
